/**
 * T8500 · Adaptive poll state — 4/8/16/32 s ladder.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 *
 * When the connectivity FSM enters DEGRADED_NO_NATS, every realtime
 * query is auto-promoted to POLL mode via a function-form
 * `refetchInterval`. Adaptive backoff:
 *   - starts at 4 s
 *   - doubles on each consecutive hash-equal response (8 → 16 → 32)
 *   - caps at 32 s
 *   - resets to 4 s on ANY payload change.
 */

export const POLL_LADDER_MS = [4_000, 8_000, 16_000, 32_000] as const;
export type PollIntervalMs = (typeof POLL_LADDER_MS)[number];

export interface PollState {
  readonly intervalMs: PollIntervalMs;
  readonly lastHash: string | null;
}

export function initialPollState(): PollState {
  return { intervalMs: POLL_LADDER_MS[0], lastHash: null };
}

/**
 * Advance the poll state. On a hash-equal consecutive response, climb
 * the ladder (capped at 32 s). On any change (or first observation),
 * reset to 4 s.
 */
export function nextPollState(prev: PollState, nextHash: string): PollState {
  if (prev.lastHash === null || prev.lastHash !== nextHash) {
    return { intervalMs: POLL_LADDER_MS[0], lastHash: nextHash };
  }
  const idx = POLL_LADDER_MS.indexOf(prev.intervalMs);
  const next = POLL_LADDER_MS[Math.min(idx + 1, POLL_LADDER_MS.length - 1)] as PollIntervalMs;
  return { intervalMs: next, lastHash: nextHash };
}

/**
 * Cheap djb2 hash over a JSON-stringifiable response. Returns a string
 * suitable for use as the `lastHash` field.
 */
export function hashResponse(value: unknown): string {
  // BigInt-safe stringify — Morfi cache rows can carry a bigint (a realtime
  // `set` of an int64 chain-ts / D6 payload field), and a bare `JSON.stringify`
  // throws `TypeError: Do not know how to serialize a BigInt`. Hashing only
  // needs a stable string, so coerce bigints to their base-10 form.
  const s = JSON.stringify(value, (_k, v) => (typeof v === 'bigint' ? v.toString() : v));
  let h = 5_381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}
